import {
  getTranslatedText,
  SuperTable,
  Row,
  ERROR_CODES,
  COMMON_CONSTANTS,
  useActivityTracker
} from '@nile/components';
import { Button, Modal, DisplayText } from '@superops/ui-design-system';
import React, { useEffect, useReducer, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import CurrencyCell from '@nile/client_management/src/Pages/Invoice/CurrencyCell';
import { GET_INVOICE_TRANSACTION_LIST } from '@nile/graphql/src/Query/client_management/partialPayments';
import { AccountClient } from '@nile/graphql';
import { DateTimeCell, TextCell } from '@nile/mangifera/src/Components/Ticket';

function PaymentMethodCell(props) {
  const paymentMethod = props.cell?.value;
  if (!paymentMethod) {
    return (
      <div className="pl5">
        <DisplayText variant="subdued">NA</DisplayText>
      </div>
    );
  }
  return <TextCell {...props} />;
}

function ErrorCell({ cell }) {
  let displayText = '';
  const mappings = cell.value;
  const intl = useIntl();

  const hasError = mappings?.some(
    (mapping) => mapping?.paymentSyncStatus === 'FAILURE'
  );

  if (!hasError) {
    return <DisplayText variant="subdued">NA</DisplayText>;
  }

  if (mappings && mappings.length > 0) {
    displayText = mappings.reduce((acc, mapping) => {
      return `${getTranslatedText(
        intl,
        ERROR_CODES[mapping.errorDetails?.code] ||
          mapping.errorDetails?.message ||
          ' '
      )}\n`;
    }, '');
  }

  return (
    <Row gap={10}>
      <SVGIcon name="WarningTriangleOutline" color="#D72C0D" size="xl" />
      {displayText}
    </Row>
  );
}

const defaultColumnCells = {
  Currency: CurrencyCell,
  Error: ErrorCell,
  DateTimeCell: DateTimeCell,
  PaymentMethodCell
};

export function PaymentSummaryModal({ open, onClose, displayId, invoiceId }) {
  const [columnGroup, setColumnGroup] = useState([
    { Header: 'Name', header: 'name', columns: [] }
  ]);

  const [transactions, setTransactions] = useState([]);

  const intl = useIntl();

  const [listLoading, setListLoading] = useState(false);
  const [listInfo, setListInfo] = useState({});

  useEffect(() => {
    useActivityTracker({
      event: 'INVOICE_RECORDED_PAYMENTS_VIEWED'
    });
  }, []);

  async function fetchData(_, pageOptions) {
    setListLoading(true);

    const resp = await AccountClient.query({
      query: GET_INVOICE_TRANSACTION_LIST,
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
      variables: {
        input: {
          page: pageOptions?.page,
          pageSize: 50,
          condition: {
            joinOperator: 'AND',
            operands: [
              {
                attribute: 'invoice.invoiceId',
                operator: 'is',
                value: invoiceId
              }
            ]
          },
          sort: [
            {
              attribute: 'createdTime',
              order: 'DESC'
            }
          ]
        }
      }
    });

    const cols = [
      {
        label: getTranslatedText(
          intl,
          'invoice.partialPayments.paymentSummary.paymentDate'
        ),
        accessor: 'payment.paymentDate',
        type: 'DateTimeCell',
        dataType: 'date'
      },
      {
        label: getTranslatedText(
          intl,
          'invoice.partialPayments.paymentSummary.paymentId'
        ),
        accessor: 'payment.displayId'
      },
      {
        label: getTranslatedText(
          intl,
          'invoice.partialPayments.paymentSummary.amountPaid'
        ),
        accessor: 'amount',
        type: 'Currency'
      },
      {
        label: getTranslatedText(
          intl,
          'invoice.partialPayments.paymentSummary.paymentMode'
        ),
        accessor: 'payment.paymentMethod.paymentMethodName',
        type: 'PaymentMethodCell'
      },
      {
        label: getTranslatedText(
          intl,
          'invoice.partialPayments.paymentSummary.reference'
        ),
        accessor: 'payment.paymentReference'
      },
      {
        label: getTranslatedText(intl, 'invoice.partialPayments.error'),
        accessor: 'mappings',
        type: 'Error'
      }
    ];

    const columns = cols.map((col, index) => {
      return {
        Header: col.label,
        header: col.label,
        accessor: col.accessor,
        accessorKey: col.accessor,
        id: `${col.accessor}_${index}`,
        disableResizing: false,
        className: 'sticky',
        type: col.type,
        // disableSortBy: !isSortable(col),
        // editable: isEditable(col),
        // disableFilters: !isFilterable(col),
        disableSortBy: true,
        editable: false,
        disableFilters: true,
        field: col,
        isBold: col.accessor === 'amount',
        dataType: col.dataType
      };
    });
    setColumnGroup([{ Header: 'Name', header: 'Name', columns }]);
    setListLoading(false);
    setTransactions(resp?.data?.getInvoiceTransactionList?.transactions);
    setListInfo(resp?.data?.getInvoiceTransactionList?.listInfo || {});
  }

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <Modal
      open={open}
      onClose={onClose}
      showCloseButton
      extraLarge
      title={`${getTranslatedText(
        intl,
        'invoice.partialPayments.paymentSummary'
      )} - ${getTranslatedText(intl, 'common.invoice')} #${displayId}`}
    >
      <Modal.Section>
        <div className="max-height-60vh overflow-y-scroll">
          <SuperTable
            context={{}}
            columns={columnGroup}
            data={transactions}
            // rowCount={100}
            refreshTable={fetchData}
            defaultColumnCells={defaultColumnCells}
            isLoading={listLoading}
            noBulkSelect
            listInfo={listInfo}
            pagination={Math.ceil(listInfo.totalCount / listInfo.pageSize) > 1}
            pages={Math.ceil(listInfo.totalCount / listInfo.pageSize) || 0}
            recordCount={listInfo.totalCount}
            activePage={listInfo.page || 0}
          />
        </div>
      </Modal.Section>
    </Modal>
  );
}

export function PaymentSummaryButton({
  transactionsList,
  displayId,
  invoiceId,
  isPlain
}) {
  const [isModalActive, toggleModal] = useReducer((s) => !s, false);
  return (
    <>
      {isModalActive && (
        <PaymentSummaryModal
          open
          onClose={toggleModal}
          transactionsList={transactionsList}
          displayId={displayId}
          invoiceId={invoiceId}
        />
      )}
      {isPlain ? (
        <div onClick={toggleModal} className="linkText">
          <FormattedMessage id="invoice.partialPayments.paymentSummary" />
        </div>
      ) : (
        <Button
          onClick={toggleModal}
          isRounded
          icon={<SVGIcon name="InvoiceLogo" />}
        >
          <FormattedMessage id="invoice.partialPayments.paymentSummary" />
        </Button>
      )}
    </>
  );
}
